// The /admin Membership section: who is a member and why, and what they pay.
// Data comes from membership_summary in the admin analytics response
// (functions/utils/adminMembership.ts). Money is gross: before processor fees
// and refunds.

import { useState, type ReactNode } from 'react';
import { AlertTriangle, Crown } from 'lucide-react';
import type { AdminUser, MembershipSummary, MoneyTotals } from '../lib/adminAnalytics';

function money(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toLocaleString()}` : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
}

// Category colours, used by the bars and their legend alike.
const CATEGORY = [
  { key: 'subscriptions_cents', label: 'Subscriptions', color: 'var(--color-accent)' },
  { key: 'one_time_cents', label: 'One-time', color: 'rgba(255, 255, 255, 0.55)' },
  { key: 'gifts_cents', label: 'Gifts', color: '#f472b6' },
] as const;

export default function AdminMembership({
  summary,
  usersById,
}: {
  summary: MembershipSummary;
  usersById: Map<string, AdminUser>;
}) {
  const { members, money: m } = summary;
  const [period, setPeriod] = useState<'last_30_days' | 'this_month' | 'all_time'>('last_30_days');
  const totals = m[period];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-textPrimary">
          <Crown size={20} className="text-accent" /> Membership
        </h2>
        <p className="text-xs text-textMuted">Gross amounts, before processor fees and refunds.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Recurring / month"
          value={money(m.mrr_cents)}
          sub={m.mrr_ending_cents > 0 ? `${money(m.mrr_ending_cents)} more is set to cancel` : 'Live subscriptions, yearly divided by 12'}
        />
        <Tile label="This month" value={money(m.this_month.total_cents)} sub={paymentsLine(m.this_month)} />
        <Tile label="Last 30 days" value={money(m.last_30_days.total_cents)} sub={paymentsLine(m.last_30_days)} />
        <Tile label="All time" value={money(m.all_time.total_cents)} sub={paymentsLine(m.all_time)} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="settings-card p-5">
          <h3 className="text-sm font-semibold text-textSecondary">Members</h3>
          <p className="mt-0.5 text-xs text-textMuted">
            {members.total} with a live membership. Each member counts once, under the first row that applies.
          </p>
          <dl className="mt-4 divide-y divide-[var(--color-borderLight)] text-sm">
            <Row label="Paying" value={members.paid} strong>
              <Sub label="Monthly" value={members.paid_monthly} />
              <Sub label="Yearly" value={members.paid_yearly} />
              <Sub label="By card" value={members.paid_stripe} />
              <Sub label="By PayPal" value={members.paid_paypal} />
              <Sub label="Set to cancel" value={members.ending} warn={members.ending > 0} />
              <Sub label="Payment failing" value={members.past_due} warn={members.past_due > 0} />
            </Row>
            <Row label="Boosting only" value={members.boost_only} hint={`${members.boosting} boosting in total, ${members.boosting - members.boost_only} of them also paying`} />
            <Row label="Gifted only" value={members.gift_only} />
          </dl>
          {members.duplicate_ids.length > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#f5b84c]/30 bg-[#f5b84c]/[0.06] p-3 text-xs leading-relaxed text-textSecondary">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-[#f5b84c]" />
              <span>
                Two subscriptions that both renew, so billed twice every month:{' '}
                {members.duplicate_ids.map((id, i) => (
                  <span key={id} className="text-textPrimary">
                    {i > 0 && ', '}@{usersById.get(id)?.username ?? id}
                  </span>
                ))}
                . Cancel the extra one in the Stripe or PayPal dashboard.
              </span>
            </div>
          )}
        </div>

        <div className="settings-card p-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-textSecondary">Where it comes from</h3>
            <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
              {([['this_month', 'Month'], ['last_30_days', '30d'], ['all_time', 'All']] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setPeriod(k)}
                  className={`rounded-md px-2.5 py-0.5 text-xs font-medium transition-colors ${period === k ? 'bg-white/[0.08] text-textPrimary' : 'text-textMuted hover:text-textSecondary'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <dl className="mt-4 space-y-2.5 text-sm">
            {CATEGORY.map(c => (
              <Share key={c.key} label={c.label} cents={totals[c.key]} total={totals.total_cents} color={c.color} />
            ))}
          </dl>
          <div className="my-4 border-t border-[var(--color-borderLight)]" />
          <dl className="space-y-2.5 text-sm">
            <Share label="Card (Stripe)" cents={totals.stripe_cents} total={totals.total_cents} color="#8b8cf6" />
            <Share label="PayPal" cents={totals.paypal_cents} total={totals.total_cents} color="#ffc439" />
            {totals.kofi_cents > 0 && (
              <Share label="Ko-fi (before Stripe)" cents={totals.kofi_cents} total={totals.total_cents} color="rgba(255, 255, 255, 0.3)" />
            )}
          </dl>
        </div>
      </div>

      <MonthlyBars months={m.months} />
    </section>
  );
}

function paymentsLine(t: MoneyTotals): string {
  return `${t.payments} payment${t.payments === 1 ? '' : 's'}`;
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="settings-card p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-textMuted">{label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-textPrimary">{value}</div>
      <div className="mt-1 text-xs text-textMuted">{sub}</div>
    </div>
  );
}

function Row({ label, value, hint, strong, children }: { label: string; value: number; hint?: string; strong?: boolean; children?: ReactNode }) {
  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <dt className={strong ? 'font-medium text-textPrimary' : 'text-textSecondary'}>{label}</dt>
        <dd className="tabular-nums font-semibold text-textPrimary">{value}</dd>
      </div>
      {hint && <p className="mt-0.5 text-xs text-textMuted">{hint}</p>}
      {children && <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1">{children}</div>}
    </div>
  );
}

function Sub({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-textMuted">{label}</span>
      <span className={`tabular-nums ${warn ? 'text-[#f5b84c]' : 'text-textSecondary'}`}>{value}</span>
    </div>
  );
}

function Share({ label, cents, total, color }: { label: string; cents: number; total: number; color: string }) {
  const pct = total > 0 ? (cents / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <dt className="flex items-center gap-2 text-textSecondary">
          <span className="h-2 w-2 rounded-full" style={{ background: color }} />
          {label}
        </dt>
        <dd className="tabular-nums text-textPrimary">
          {money(cents)}
          <span className="ml-2 inline-block w-9 text-right text-xs text-textMuted">{Math.round(pct)}%</span>
        </dd>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.05]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function MonthlyBars({ months }: { months: MembershipSummary['money']['months'] }) {
  const totals = months.map(mo => mo.subscriptions_cents + mo.one_time_cents + mo.gifts_cents);
  const max = Math.max(1, ...totals);
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? months.length - 1;
  const cur = months[shown];

  return (
    <div className="settings-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-textSecondary">By month</h3>
        <p className="text-xs text-textMuted">
          <span className="text-textPrimary">{monthLabel(cur.month)} {cur.month.slice(0, 4)}</span>
          {' · '}
          {money(totals[shown])}
          {CATEGORY.map(c => cur[c.key] > 0 && (
            <span key={c.key}>{' · '}{c.label.toLowerCase()} {money(cur[c.key])}</span>
          ))}
        </p>
      </div>
      <div className="mt-4 flex h-36 items-end gap-1.5" onMouseLeave={() => setHover(null)}>
        {months.map((mo, i) => (
          <div
            key={mo.month}
            className="flex h-full flex-1 cursor-default flex-col items-center justify-end gap-1.5"
            onMouseEnter={() => setHover(i)}
          >
            <div
              className={`flex w-full max-w-10 flex-col-reverse overflow-hidden rounded-md transition-opacity ${hover !== null && hover !== i ? 'opacity-50' : ''}`}
              style={{ height: `${(totals[i] / max) * 100}%`, minHeight: totals[i] > 0 ? 3 : 0 }}
            >
              {CATEGORY.map(c => (
                <div key={c.key} style={{ height: `${totals[i] > 0 ? (mo[c.key] / totals[i]) * 100 : 0}%`, background: c.color }} />
              ))}
            </div>
            <span className={`text-[10px] ${i === shown ? 'text-textSecondary' : 'text-textMuted'}`}>{monthLabel(mo.month)}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-textMuted">
        {CATEGORY.map(c => (
          <span key={c.key} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}
